// ---- the graph: the factory at work, as nodes and links --------------------------
//
// Prime sits in the middle; every coding agent hangs off it (Prime looks after
// all of them); an agent links to the task it works for, and a task to its
// project. An agent with no task but a project links to the project. Around
// that, the rest of the factory: the git issues in play (and a few more per
// project), the pages being used (what Prime has shown, the pages a working
// agent's project hands it), the pipeline (builds of main under way, a ship on
// its way, prod and staging), Prime's cards waiting for the owner, and the
// cluster underneath (nodes with their load, the sandboxes on them, pods and
// Argo apps that need a look) with today's spend. Layers choose what is in it;
// a mode (Everything, Work, Pipeline, Infra, Approvals) chooses what is in view
// and how it is arranged (`forMode`, `layoutMode`: pure). views/AgentGraph.tsx
// draws it.

import type { AgentRunResource } from '@/kinds/agentrun/apply'
import { podWords } from './podWords'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import type { PageResource } from '@/kinds/page/apply'
import type { GitIssueResource } from '@/kinds/git/apply'
import type { SuggestionResource } from '@/kinds/suggestion'
import type { AgentStats, Build, Builds, ClusterSandbox, ClusterSummary } from '@/db/api'
import { isOpen } from '@/kinds/task/schema'
import { taskOf } from './board'
import { pipelineOf, runsVersion, shipping, type Pipeline } from './pipeline'

export type GraphNodeKind =
  | 'prime'
  | 'agent'
  | 'task'
  | 'project'
  | 'issue'
  | 'doc'
  | 'build'
  | 'ship'
  | 'env'
  | 'card'
  | 'node' // a cluster node, with its load
  | 'sandbox' // an agent's sandbox pod, on a node
  | 'problem' // a pod that needs a look
  | 'app' // an Argo CD application and its health
  | 'spend' // today's agent spend and turns

export interface GraphNode {
  id: string
  kind: GraphNodeKind
  label: string
  /** an agent's phase (Working, Waiting, …), a task's state, a build's state, a ship's stage */
  phase?: string
  run?: AgentRunResource
  task?: TaskResource
  project?: ProjectResource
  issue?: GitIssueResource
  page?: PageResource
  build?: Build
  ship?: Pipeline
  /** an environment: its name and the version it answers with */
  env?: { name: string; version: string }
  card?: SuggestionResource
  host?: ClusterSummary['nodes'][number]
  sandbox?: ClusterSandbox
  problem?: ClusterSummary['problems'][number]
  app?: ClusterSummary['apps'][number]
  spend?: { usd: number; turns: number; notional: boolean }
}

export interface GraphLink {
  source: string
  target: string
  kind:
    | 'looks-after' // Prime → agent
    | 'works-on' // agent → task or project
    | 'part-of' // task → project
    | 'tracks' // issue → the task, agent or project it is about
    | 'uses' // Prime, agent or project → a page it reads
    | 'builds' // agent (its merge) or Prime → a build of main
    | 'deploys' // build → staging, ship → prod, staging → prod
    | 'asks' // a waiting card → what it concerns
    | 'runs-on' // agent → its sandbox, sandbox or problem pod → its node
    | 'spends' // Prime → today's spend
}

export interface AgentGraph {
  nodes: GraphNode[]
  links: GraphLink[]
}

/** How many open issues beyond the ones in play each project shows. */
export const ISSUES_PER_PROJECT = 3
/** How many of a project's pages show for the agents working on it. */
export const DOCS_PER_PROJECT = 5

const over = (phase: string) => phase === 'Ended' || phase === 'Failed'
const WEEK = 7 * 86_400_000

const repoOf = (url: string) => url.replace(/^https?:\/\/[^/]+\//, '').replace(/\.git$/, '')
const short = (sha: string) => sha.slice(0, 7)

/** Prime: the newest lead session that is not over. */
export const leadOf = (
  runs: AgentRunResource[],
  phaseOf: (r: AgentRunResource) => string = (r) => r.spec.status,
) =>
  runs
    .filter((r) => r.spec.template === 'prime' && !r.deleted_at && !over(phaseOf(r)))
    .sort((a, b) => (b.spec.started_at || '').localeCompare(a.spec.started_at || ''))[0]

/** A ship in words: "Shipping c3184fd: restarting". */
export const shipLabel = (p: Pipeline) => {
  const stage = p.stages.find((s) => s.state === 'active' || s.state === 'failed')
  const words: Record<string, string> = {
    tagged: 'tagged',
    picked: 'waiting for Argo CD',
    applied: 'applying',
    restarting: 'restarting',
    live: 'going live',
  }
  return `Shipping ${short(p.sha)}${stage ? `: ${stage.state === 'failed' ? 'stuck' : words[stage.key]}` : ''}`
}

/** The pull request number a squash merge's subject names ("Title (#22)"). */
const prOfNote = (note = '') => {
  const m = /\(#(\d+)\)\s*$/.exec(note) ?? /#(\d+)\b/.exec(note)
  return m ? Number(m[1]) : 0
}

/**
 * The graph now. `week` keeps agents that ended in the last seven days (else
 * only live ones); `phaseOf` gives an agent's current phase (the streamed one
 * when newer than the mirror). Each of `issues`, `pages`, `builds` and `cards`
 * is a layer: left out, it is not drawn.
 */
export const agentGraph = ({
  runs,
  tasks,
  projects,
  projectOf,
  now,
  week,
  phaseOf = (r) => r.spec.status,
  issues,
  pages,
  builds,
  pipeline = true,
  envs = true,
  cards,
  infra,
  stats,
}: {
  runs: AgentRunResource[]
  tasks: TaskResource[]
  projects: ProjectResource[]
  /** child id → the id it belongs_to (tasks and pages to projects) */
  projectOf: Map<string, string>
  now: number
  week: boolean
  phaseOf?: (r: AgentRunResource) => string
  /** git issue mirrors (open ones are drawn) */
  issues?: GitIssueResource[]
  /** pages: what Prime has shown, and the pages of a project an agent works for */
  pages?: PageResource[]
  /** GET /api/builds, or null while it cannot be read */
  builds?: Builds | null
  /** with `builds`: draw the builds and a ship on their way (the pipelines layer) */
  pipeline?: boolean
  /** with `builds`: draw prod and staging (the environments layer) */
  envs?: boolean
  /** Prime's cards: those pending on the lead's session are drawn */
  cards?: SuggestionResource[]
  /** GET /api/cluster, with the layers that draw from it; null while it cannot be read */
  infra?: { cluster: ClusterSummary | null; nodes: boolean; sandboxes: boolean; health: boolean }
  /** GET /api/agents/stats?days=1: today's spend and turns (the spend layer) */
  stats?: AgentStats | null
}): AgentGraph => {
  const nodes = new Map<string, GraphNode>()
  const links: GraphLink[] = []
  const linked = new Set<string>()
  const link = (source: string, target: string, kind: GraphLink['kind']) => {
    const key = `${source}>${target}`
    if (source === target || linked.has(key)) return
    linked.add(key)
    links.push({ source, target, kind })
  }
  const taskById = new Map(tasks.map((t) => [t.id, t]))
  const projectById = new Map(projects.map((p) => [p.id, p]))

  const lead = leadOf(runs, phaseOf)
  if (lead)
    nodes.set(lead.id, { id: lead.id, kind: 'prime', label: 'Prime', phase: phaseOf(lead), run: lead })

  const addProject = (id: string) => {
    const p = projectById.get(id)
    if (!p || p.deleted_at) return undefined
    if (!nodes.has(id)) nodes.set(id, { id, kind: 'project', label: p.spec.title, project: p })
    return id
  }
  const addTask = (id: string) => {
    const t = taskById.get(id)
    if (!t || t.deleted_at) return undefined
    if (!nodes.has(id)) {
      nodes.set(id, { id, kind: 'task', label: t.spec.title, phase: t.status.state, task: t })
      const pid = projectOf.get(id)
      if (pid && addProject(pid)) link(id, pid, 'part-of')
    }
    return id
  }

  // ---- agents, their tasks and projects ----
  /** projects a live agent works for: their pages are its brief */
  const working = new Set<string>()
  const agents: AgentRunResource[] = []
  for (const r of runs) {
    if (r.spec.template === 'prime' || r.spec.source.provider !== 'factory' || r.deleted_at) continue
    const phase = phaseOf(r)
    if (over(phase)) {
      const ended = Date.parse(r.spec.ended_at || r.spec.updated_at || '') || 0
      if (!week || now - ended > WEEK) continue
    }
    agents.push(r)
    nodes.set(r.id, { id: r.id, kind: 'agent', label: r.spec.title || r.spec.name || 'Agent', phase, run: r })
    if (lead) link(lead.id, r.id, 'looks-after')
    const tid = taskOf(r)
    const target = (tid && addTask(tid)) || (r.spec.project_id && addProject(r.spec.project_id))
    if (target) link(r.id, target, 'works-on')
    if (!over(phase)) {
      const pid =
        r.spec.project_id && projectById.has(r.spec.project_id)
          ? r.spec.project_id
          : tid && projectOf.get(tid)
      if (pid && nodes.has(pid)) working.add(pid)
    }
  }

  // ---- issues in play: tied to a drawn agent, or to an open task ----
  const openIssues = (issues ?? []).filter((i) => !i.deleted_at && i.status.state === 'open')
  const placed = new Set<string>()
  const addIssue = (i: GitIssueResource) => {
    if (!nodes.has(i.id))
      nodes.set(i.id, { id: i.id, kind: 'issue', label: `#${i.spec.number} ${i.spec.title}`, issue: i })
    placed.add(i.id)
  }
  for (const i of openIssues) {
    const direct = agents.filter(
      (r) => r.spec.issue_id === i.id || (r.spec.source_kind === 'issue' && r.spec.source_ref === i.id),
    )
    const tid = i.status.task_id
    const task = tid ? taskById.get(tid) : undefined
    const viaAgent = !!tid && agents.some((r) => taskOf(r) === tid)
    const openTask = !!task && !task.deleted_at && isOpen(task.status.state)
    if (!direct.length && !viaAgent && !openTask) continue
    addIssue(i)
    for (const r of direct) link(i.id, r.id, 'tracks')
    if (tid && (viaAgent || openTask) && addTask(tid)) link(i.id, tid, 'tracks')
  }

  // ---- the pipeline: builds of main under way, a ship on its way, the environments ----
  if (builds) {
    const prod = builds.versions.prod ?? ''
    const staging = builds.versions.staging ?? ''
    const envId = (name: string) => `env:${name}`
    if (staging && envs)
      nodes.set(envId('staging'), {
        id: envId('staging'),
        kind: 'env',
        // the version is in its panel: the graph names the place
        label: 'Staging',
        env: { name: 'staging', version: staging },
      })
    if (prod && envs)
      nodes.set(envId('prod'), {
        id: envId('prod'),
        kind: 'env',
        label: 'Prod',
        env: { name: 'prod', version: prod },
      })
    if (staging && prod && envs) link(envId('staging'), envId('prod'), 'deploys')
    const repo = builds.repo
    for (const b of pipeline ? builds.builds : []) {
      if (b.state !== 'queued' && b.state !== 'building') continue
      const id = `build:${b.name}`
      nodes.set(id, {
        id,
        kind: 'build',
        // named by its commit's subject (the sha is in its panel)
        label: `${b.state === 'queued' ? 'Queued' : 'Building'}: ${b.note || short(b.sha)}`,
        phase: b.state,
        build: b,
      })
      // the agent whose merge made the commit, when its subject names the PR
      const pr = prOfNote(b.note)
      const by = pr
        ? agents.find((r) => r.spec.pr_number === pr && (!repo || repoOf(r.spec.repo_url) === repo))
        : undefined
      if (by) link(by.id, id, 'builds')
      else if (lead) link(lead.id, id, 'builds')
      if (staging && envs) link(id, envId('staging'), 'deploys')
    }
    const p = pipeline && shipping(builds) ? pipelineOf(builds, now) : null
    if (p) {
      const failed = p.stages.some((s) => s.state === 'failed')
      const active = p.stages.find((s) => s.state === 'active' || s.state === 'failed')
      nodes.set('ship', {
        id: 'ship',
        kind: 'ship',
        label: shipLabel(p),
        phase: failed ? 'failed' : (active?.key ?? 'tagged'),
        ship: p,
      })
      if (prod && envs) link('ship', envId('prod'), 'deploys')
      else if (lead) link(lead.id, 'ship', 'builds')
    }
  }

  // ---- the cluster: nodes and their load, the sandboxes on them, what needs a look ----
  const cluster = infra?.cluster
  if (infra && cluster) {
    const hosts = new Map(cluster.nodes.map((n) => [n.name, n]))
    /** a node is drawn when asked for, or when a sandbox or pod on it is */
    const ensureNode = (name?: string) => {
      const h = name ? hosts.get(name) : undefined
      if (!h) return undefined
      const id = `node:${h.name}`
      if (!nodes.has(id))
        nodes.set(id, { id, kind: 'node', label: h.name, phase: h.ready ? 'ready' : 'down', host: h })
      return id
    }
    if (infra.nodes) for (const h of cluster.nodes) ensureNode(h.name)
    if (infra.sandboxes) {
      for (const sb of cluster.sandboxes ?? []) {
        const id = `sandbox:${sb.name}`
        nodes.set(id, {
          id,
          kind: 'sandbox',
          label: `${sb.template} sandbox`,
          phase: sb.phase === 'Running' && sb.ready ? 'ready' : sb.phase,
          sandbox: sb,
        })
        const host = ensureNode(sb.node)
        if (host) link(id, host, 'runs-on')
        // the agent whose session it holds, when the pod's name carries the session's
        const owner = [...nodes.values()].find(
          (n) =>
            (n.kind === 'agent' || n.kind === 'prime') &&
            !!n.run?.spec.name &&
            sb.name.includes(n.run.spec.name),
        )
        if (owner) link(owner.id, id, 'runs-on')
      }
    }
    if (infra.health) {
      for (const p of cluster.problems) {
        const id = `problem:${p.namespace}/${p.name}`
        nodes.set(id, {
          id,
          kind: 'problem',
          label: p.reason ? `${p.name}: ${podWords(p.reason)}` : p.name,
          phase: p.phase,
          problem: p,
        })
        const host = ensureNode(p.node)
        if (host) link(id, host, 'runs-on')
      }
      // apps: the ones off, and the two that are alfieOS's own
      const own: Record<string, string> = { alfieos: 'env:prod', 'alfieos-staging': 'env:staging' }
      for (const a of cluster.apps) {
        const off = appIsOff(a)
        if (!off && !own[a.name]) continue
        const id = `app:${a.name}`
        nodes.set(id, { id, kind: 'app', label: a.name, phase: a.health, app: a })
        const env = own[a.name]
        if (env && nodes.has(env)) link(id, env, 'deploys')
      }
    }
  }

  // ---- today's spend, hung on Prime ----
  if (stats && (stats.totals.usd > 0 || stats.totals.turns > 0)) {
    const { usd, turns } = stats.totals
    const money = `${stats.notional ? '≈' : ''}$${usd < 10 ? usd.toFixed(2) : usd.toFixed(0)}`
    nodes.set('spend', {
      id: 'spend',
      kind: 'spend',
      label: `${money} today, ${turns} ${turns === 1 ? 'turn' : 'turns'}`,
      spend: { usd, turns, notional: stats.notional },
    })
    if (lead) link(lead.id, 'spend', 'spends')
  }

  // ---- Prime's cards waiting for the owner, on what they concern ----
  if (lead && cards) {
    const settled = (repo: string, n: number) =>
      runs.some(
        (r) =>
          r.spec.pr_number === n &&
          repoOf(r.spec.repo_url) === repo &&
          (r.spec.pr_state === 'merged' || r.spec.pr_state === 'closed'),
      )
    const resolve = (id: unknown) => {
      if (typeof id !== 'string' || !id) return undefined
      return nodes.has(id) ? id : addTask(id) || addProject(id)
    }
    for (const s of cards) {
      if (s.deleted_at || s.status.state !== 'pending' || s.spec.session_id !== lead.id) continue
      if (s.spec.type !== 'ask' && !s.spec.ops.length) continue
      // a merge card whose pull request is no longer open has nothing left to do (LeadCards)
      const stale = s.spec.ops.some((op) => {
        const a = (op.args ?? {}) as { repo?: string; number?: number }
        return op.type === 'merge_pr' && !!a.repo && !!a.number && settled(a.repo, a.number)
      })
      if (stale) continue
      const targets = new Set<string>()
      for (const op of s.spec.ops) {
        const a = (op.args ?? {}) as Record<string, unknown>
        if (op.type === 'merge_pr') {
          const r = agents.find((x) => x.spec.pr_number === a.number && repoOf(x.spec.repo_url) === a.repo)
          if (r) targets.add(r.id)
        } else if (op.type === 'release') {
          const sha = typeof a.sha === 'string' ? a.sha : ''
          const shipNode = nodes.get('ship')
          const build = [...nodes.values()].find(
            (n) => n.kind === 'build' && runsVersion(n.build?.sha ?? '', sha),
          )
          const t =
            shipNode && runsVersion(shipNode.ship?.sha ?? '', sha)
              ? 'ship'
              : (build?.id ?? (nodes.has('env:prod') ? 'env:prod' : undefined))
          if (t) targets.add(t)
        } else {
          const t = resolve(op.id) ?? resolve(a.task_id) ?? resolve(a.project_id) ?? resolve(a.parent_id)
          if (t) targets.add(t)
        }
      }
      const id = s.id
      nodes.set(id, { id, kind: 'card', label: s.spec.title, card: s })
      if (!targets.size) targets.add(lead.id)
      for (const t of targets) link(id, t, 'asks')
    }
  }

  // ---- pages in use: what Prime has shown, and the brief of a project an agent works for ----
  if (pages) {
    const pageById = new Map(pages.filter((p) => !p.deleted_at).map((p) => [p.id, p]))
    const addDoc = (p: PageResource) => {
      if (!nodes.has(p.id))
        nodes.set(p.id, { id: p.id, kind: 'doc', label: p.spec.title || 'Untitled', page: p })
      return p.id
    }
    for (const a of lead?.status.artifacts ?? []) {
      const p = pageById.get(a.id)
      if (p && lead) link(lead.id, addDoc(p), 'uses')
    }
    for (const pid of working) {
      const under = [...pageById.values()]
        .filter((p) => projectOf.get(p.id) === pid)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        .slice(0, DOCS_PER_PROJECT)
      for (const p of under) link(pid, addDoc(p), 'uses')
    }
  }

  // ---- the rest of a drawn project's open issues, a few each, newest first ----
  if (issues) {
    const shown = new Map<string, number>()
    const rest = openIssues
      .filter((i) => !placed.has(i.id))
      .sort((a, b) =>
        (b.spec.source.updated_at ?? b.updated_at).localeCompare(a.spec.source.updated_at ?? a.updated_at),
      )
    const drawnProjects = [...nodes.values()].filter((n) => n.kind === 'project' && n.project)
    for (const i of rest) {
      const p = drawnProjects.find(
        (n) =>
          (n.project?.spec.repos ?? []).includes(i.spec.repo) && (shown.get(n.id) ?? 0) < ISSUES_PER_PROJECT,
      )
      if (!p) continue
      shown.set(p.id, (shown.get(p.id) ?? 0) + 1)
      addIssue(i)
      link(i.id, p.id, 'tracks')
    }
  }

  return { nodes: [...nodes.values()], links }
}

/** The ids one hop from `id`, itself included: what lights up on hover. */
export const neighbours = (g: AgentGraph, id: string): Set<string> => {
  const out = new Set([id])
  for (const l of g.links) {
    if (l.source === id) out.add(l.target)
    if (l.target === id) out.add(l.source)
  }
  return out
}

// ---- layers: what the graph holds ----------------------------------------------

export type Layer =
  'approvals' | 'issues' | 'docs' | 'pipelines' | 'envs' | 'nodes' | 'sandboxes' | 'health' | 'spend'
export type Layers = Record<Layer, boolean>

/** The node kinds a layer draws. */
export const LAYER_KINDS: Record<Layer, GraphNodeKind[]> = {
  approvals: ['card'],
  issues: ['issue'],
  docs: ['doc'],
  pipelines: ['build', 'ship'],
  envs: ['env'],
  nodes: ['node'],
  sandboxes: ['sandbox'],
  health: ['problem', 'app'],
  spend: ['spend'],
}

// ---- modes: what is in view, and how it is arranged ----------------------------

export type GraphMode = 'everything' | 'work' | 'pipeline' | 'infra' | 'approvals'

export const MODES: { key: GraphMode; label: string; blurb: string; empty: string }[] = [
  {
    key: 'everything',
    label: 'Everything',
    blurb: 'All of it, free-floating',
    empty: 'Nothing to draw yet.',
  },
  {
    key: 'work',
    label: 'Work',
    blurb: 'Projects, tasks, agents, then issues and pages, in columns',
    empty: 'Nothing to draw yet.',
  },
  {
    key: 'pipeline',
    label: 'Pipeline',
    blurb: 'Builds, staging, a ship under way, prod, left to right, each with its agent',
    empty: 'No builds or environments to show. Prod and staging appear once the factory answers.',
  },
  {
    key: 'infra',
    label: 'Infra',
    blurb: 'Each node with the sandboxes, pods and apps on it',
    empty: 'The cluster has not answered. Nodes, sandboxes and apps show here when it does.',
  },
  {
    key: 'approvals',
    label: 'Approvals',
    blurb: 'What is waiting for you, grouped by kind',
    empty: 'Nothing waiting for you.',
  },
]

/** The kinds a mode shows (null: all of them). */
const MODE_KINDS: Record<GraphMode, Set<GraphNodeKind> | null> = {
  everything: null,
  work: new Set<GraphNodeKind>(['prime', 'agent', 'task', 'project', 'issue', 'doc', 'spend']),
  pipeline: new Set<GraphNodeKind>(['prime', 'agent', 'build', 'ship', 'env', 'app']),
  infra: new Set<GraphNodeKind>(['prime', 'agent', 'sandbox', 'node', 'problem', 'app', 'env']),
  approvals: new Set<GraphNodeKind>(['prime', 'agent', 'task', 'project', 'build', 'ship', 'env', 'card']),
}

/** Layers a mode is made of: on whatever the device's toggles say. */
const MODE_CORE: Record<GraphMode, Layer[]> = {
  everything: [],
  work: [],
  pipeline: ['pipelines', 'envs'],
  infra: ['nodes', 'sandboxes', 'health', 'envs'],
  approvals: ['approvals'],
}

/** Whether a layer's toggle means anything in a mode (its kinds are among the mode's). */
export const layerInMode = (layer: Layer, mode: GraphMode) => {
  const kinds = MODE_KINDS[mode]
  return !kinds || LAYER_KINDS[layer].some((k) => kinds.has(k))
}

/** Whether a mode needs a layer: its toggle is on and cannot be turned off there. */
export const layerRequired = (layer: Layer, mode: GraphMode) => MODE_CORE[mode].includes(layer)

/** The layers in force: the device's toggles, with a mode's own layers on. */
export const effectiveLayers = (layers: Layers, mode: GraphMode): Layers => {
  const out = { ...layers }
  for (const l of MODE_CORE[mode]) out[l] = true
  return out
}

/** The part of the graph a mode shows. Everything is the graph itself. */
export const forMode = (g: AgentGraph, mode: GraphMode): AgentGraph => {
  const kinds = MODE_KINDS[mode]
  if (!kinds) return g
  let keep: Set<string>
  if (mode === 'approvals') {
    // the cards, what each concerns, and Prime
    const cards = new Set(g.nodes.filter((n) => n.kind === 'card').map((n) => n.id))
    keep = new Set(cards)
    for (const l of g.links) {
      if (cards.has(l.source)) keep.add(l.target)
      if (cards.has(l.target)) keep.add(l.source)
    }
    for (const n of g.nodes) if (n.kind === 'prime') keep.add(n.id)
  } else {
    keep = new Set(g.nodes.filter((n) => kinds.has(n.kind)).map((n) => n.id))
    // an agent or Prime shows only for what else it is linked to in this mode
    const anchored = (id: string) =>
      g.links.some((l) => {
        const other = l.source === id ? l.target : l.target === id ? l.source : ''
        const n = other ? g.nodes.find((x) => x.id === other) : undefined
        return !!n && keep.has(n.id) && n.kind !== 'agent' && n.kind !== 'prime'
      })
    if (mode !== 'work')
      for (const n of g.nodes)
        if ((n.kind === 'agent' || n.kind === 'prime') && !anchored(n.id)) keep.delete(n.id)
  }
  const nodes = g.nodes.filter((n) => keep.has(n.id) && kinds.has(n.kind))
  const ids = new Set(nodes.map((n) => n.id))
  return { nodes, links: g.links.filter((l) => ids.has(l.source) && ids.has(l.target)) }
}

export interface Pt {
  x: number
  y: number
}

/** What a waiting card is, for Approvals' columns: its kind of ask, in the order they are read. */
const CARD_GROUPS = ['ask', 'merge', 'start', 'release', 'change'] as const
type CardGroup = (typeof CARD_GROUPS)[number]
export const cardGroup = (n: GraphNode): CardGroup => {
  const ops = n.card?.spec.ops ?? []
  if (ops.some((o) => o.type === 'merge_pr')) return 'merge'
  if (ops.some((o) => o.type === 'start_agent')) return 'start'
  if (ops.some((o) => o.type === 'release')) return 'release'
  return n.card?.spec.type === 'ask' ? 'ask' : 'change'
}
const GROUP_NAMES: Record<CardGroup, string> = {
  ask: 'Questions',
  merge: 'Merges',
  start: 'Agents to start',
  release: 'Releases',
  change: 'Changes',
}

/** Where a mode puts a node (its column or row), and what each column is called. */
type Rank = { axis: 'down' | 'right'; of: (n: GraphNode) => number; names: Record<number, string> }
const byKind = (ranks: Partial<Record<GraphNodeKind, number>>, fallback: number) => (n: GraphNode) =>
  ranks[n.kind] ?? fallback
const RANKS: Record<Exclude<GraphMode, 'everything'>, Rank> = {
  // columns, left to right: projects, their tasks, the agents on them, then issues and pages
  work: {
    axis: 'right',
    of: byKind({ project: 0, task: 1, prime: 2, spend: 2, agent: 2, issue: 3, doc: 3 }, 3),
    names: { 0: 'Projects', 1: 'Tasks', 2: 'Agents', 3: 'Issues and pages' },
  },
  // left to right as a change goes out: its agent, the build, staging, a ship under way, prod
  pipeline: {
    axis: 'right',
    of: (n) =>
      n.kind === 'build'
        ? 1
        : n.kind === 'ship'
          ? 3
          : n.kind === 'env'
            ? n.env?.name === 'prod'
              ? 4
              : 2
            : n.kind === 'app'
              ? 5
              : 0,
    names: { 0: 'Agents', 1: 'Builds', 2: 'Staging', 3: 'Ship', 4: 'Prod', 5: 'Argo apps' },
  },
  // each node, then what is on it: sandboxes, pods to look at, apps; then the agents and environments they serve
  infra: {
    axis: 'right',
    of: byKind({ node: 0, sandbox: 1, problem: 1, app: 1, prime: 2, agent: 2, env: 3 }, 1),
    names: { 0: 'Nodes', 1: 'On the nodes', 2: 'Agents', 3: 'Prod and staging' },
  },
  // what is waiting, grouped by kind of ask, then what they concern, then Prime (laid out as a list: approvalsLayout)
  approvals: {
    axis: 'down',
    of: (n) => (n.kind === 'card' ? CARD_GROUPS.indexOf(cardGroup(n)) : n.kind === 'prime' ? 6 : 5),
    names: {
      ...Object.fromEntries(CARD_GROUPS.map((g, i) => [i, GROUP_NAMES[g]])),
      5: 'They concern',
    },
  },
}

/**
 * Approvals as a list, read like a to-do: the cards one under another, grouped
 * by kind with a heading each, their titles beside them (and their ✓ and ×
 * before them); what they concern in a column to the right, each level with the
 * cards that link to it; Prime beyond. A row is tall enough for a fingertip's
 * buttons at the zoom a phone's width gives it.
 */
export const APPROVAL_ROW = 76
const APPROVAL_GROUP_GAP = 52
/** where what the cards concern stands, right of the cards' titles */
export const APPROVAL_CONCERN_X = 420
const approvalsLayout = (g: AgentGraph): Record<string, Pt> => {
  const out: Record<string, Pt> = {}
  const order = (n: GraphNode) => CARD_GROUPS.indexOf(cardGroup(n))
  const cards = g.nodes
    .filter((n) => n.kind === 'card')
    .sort((a, b) => order(a) - order(b) || a.label.localeCompare(b.label))
  let y = 0
  let last = -1
  for (const c of cards) {
    if (last >= 0 && order(c) !== last) y += APPROVAL_GROUP_GAP
    last = order(c)
    out[c.id] = { x: 0, y }
    y += APPROVAL_ROW
  }
  const cardYs = cards.map((c) => out[c.id]!.y)
  const mid = cardYs.length ? (Math.min(...cardYs) + Math.max(...cardYs)) / 2 : 0
  const others = g.nodes.filter((n) => n.kind !== 'card' && n.kind !== 'prime')
  const want = new Map(
    others.map((n) => {
      const ys = g.links
        .filter((l) => l.source === n.id || l.target === n.id)
        .map((l) => out[l.source === n.id ? l.target : l.source]?.y)
        .filter((v): v is number => v !== undefined)
      return [n.id, ys.length ? ys.reduce((a, v) => a + v, 0) / ys.length : mid]
    }),
  )
  // level with their cards, in that order, never closer than a row apart
  let floor = -Infinity
  for (const n of [...others].sort(
    (a, b) => want.get(a.id)! - want.get(b.id)! || a.label.localeCompare(b.label),
  )) {
    const at = Math.max(want.get(n.id)!, floor)
    out[n.id] = { x: APPROVAL_CONCERN_X, y: at }
    floor = at + APPROVAL_ROW * 0.8
  }
  for (const n of g.nodes)
    if (n.kind === 'prime')
      out[n.id] = { x: others.length ? APPROVAL_CONCERN_X + 200 : APPROVAL_CONCERN_X, y: mid }
  return out
}

/** Spacing in a layered layout: between nodes across a rank, between a rank's rows, and between ranks. */
const CROSS = { down: 150, right: 64 }
const PER_ROW = { down: 8, right: 8 }
const ROW_GAP = { down: 70, right: 170 }
const RANK_GAP = { down: 110, right: 150 }
const STAGGER = 22

/**
 * Where each node of a mode's view sits (null for Everything, which floats).
 * Nodes go in ranks by kind, each rank a column (left to right) or a row; within
 * a rank they are ordered by the mean place of what they link to, so links stay
 * short; a rank wider than a row wraps, and a column is as far from the next as
 * its longest name needs, so titles read whole.
 */
export const layoutMode = (g: AgentGraph, mode: GraphMode): Record<string, Pt> | null => {
  if (mode === 'everything') return null
  if (mode === 'approvals') return approvalsLayout(g)
  const { axis, of } = RANKS[mode]
  const byRank = new Map<number, GraphNode[]>()
  for (const n of [...g.nodes].sort((a, b) => a.kind.localeCompare(b.kind) || a.label.localeCompare(b.label)))
    byRank.set(of(n), [...(byRank.get(of(n)) ?? []), n])
  const ranks = [...byRank.keys()].sort((a, b) => a - b).map((r) => byRank.get(r)!)

  const adj = new Map<string, string[]>()
  for (const l of g.links) {
    adj.set(l.source, [...(adj.get(l.source) ?? []), l.target])
    adj.set(l.target, [...(adj.get(l.target) ?? []), l.source])
  }
  const place = new Map<string, number>() // 0..1 across its rank
  const settle = () => {
    for (const r of ranks) r.forEach((n, i) => place.set(n.id, (i + 0.5) / r.length))
  }
  settle()
  const sweep = (r: GraphNode[]) => {
    const key = new Map(
      r.map((n) => {
        const near = (adj.get(n.id) ?? []).filter((id) => place.has(id) && !r.some((m) => m.id === id))
        const own = place.get(n.id) ?? 0.5
        // what links to nothing in another rank goes last, after what hangs off the ranks beside it
        return [
          n.id,
          near.length ? near.reduce((a, id) => a + (place.get(id) ?? 0.5), 0) / near.length : 1 + own,
        ]
      }),
    )
    r.sort((a, b) => key.get(a.id)! - key.get(b.id)!)
    settle()
  }
  for (let i = 0; i < 4; i++) {
    for (const r of ranks) sweep(r)
    for (const r of [...ranks].reverse()) sweep(r)
  }

  const out: Record<string, Pt> = {}
  let v = 0
  for (const r of ranks) {
    const chunks: GraphNode[][] = []
    for (let i = 0; i < r.length; i += PER_ROW[axis]) chunks.push(r.slice(i, i + PER_ROW[axis]))
    const longest = Math.max(...r.map((n) => n.label.length))
    const wide = Math.min(300, Math.max(0, longest * 6.4 + 24))
    // down the page a rank is as open as its longest name; across it a column is as wide
    const space = axis === 'down' ? Math.max(CROSS.down, wide) : CROSS.right
    const rowGap = axis === 'right' ? Math.max(ROW_GAP.right, wide + 40) : ROW_GAP.down
    chunks.forEach((chunk, c) => {
      chunk.forEach((n, i) => {
        const cross = (i - (chunk.length - 1) / 2) * space
        const along = v + c * rowGap + (chunk.length > 4 && i % 2 ? STAGGER : 0)
        out[n.id] = axis === 'down' ? { x: cross, y: along } : { x: along, y: cross }
      })
    })
    v +=
      (chunks.length - 1) * rowGap +
      (r.length > 4 ? STAGGER : 0) +
      (axis === 'right' ? Math.max(RANK_GAP.right, wide) : RANK_GAP.down)
  }
  return out
}

/**
 * The name of each column (or row) of a mode's layout, where it begins: drawn
 * above the nodes it heads. None for Everything, and none for a rank that is empty.
 */
export const headingsFor = (
  g: AgentGraph,
  mode: GraphMode,
  at: Record<string, Pt>,
): { label: string; x: number; y: number; anchor?: 'start' | 'middle' }[] => {
  if (mode === 'everything') return []
  const { axis, of, names } = RANKS[mode]
  if (mode === 'approvals') {
    // a list: each group's name over its first card, starting where the titles do
    const first = new Map<number, Pt>()
    const concern: Pt[] = []
    for (const n of g.nodes) {
      const p = at[n.id]
      if (!p) continue
      const r = of(n)
      if (n.kind === 'card') {
        const had = first.get(r)
        if (!had || p.y < had.y) first.set(r, p)
      } else if (n.kind !== 'prime') concern.push(p)
    }
    return [
      ...[...first.entries()]
        .sort(([a], [b]) => a - b)
        .map(([r, p]) => ({ label: names[r]!, x: p.x - 10, y: p.y - 30, anchor: 'start' as const })),
      ...(concern.length
        ? [{ label: names[5]!, x: APPROVAL_CONCERN_X, y: Math.min(...concern.map((p) => p.y)) - 34 }]
        : []),
    ]
  }
  const seen = new Map<number, Pt[]>()
  for (const n of g.nodes) if (at[n.id]) seen.set(of(n), [...(seen.get(of(n)) ?? []), at[n.id]!])
  return [...seen.entries()]
    .filter(([r]) => names[r])
    .sort(([a], [b]) => a - b)
    .map(([r, ps]) =>
      axis === 'right'
        ? { label: names[r]!, x: ps[0]!.x, y: Math.min(...ps.map((p) => p.y)) - 52 }
        : { label: names[r]!, x: Math.min(...ps.map((p) => p.x)) - 90, y: ps[0]!.y },
    )
}

/**
 * A view (zoom and offset) that frames the points in a box, with room for labels:
 * `pad` in graph units, or in screen pixels with `screen` (a phone draws its labels
 * at a fixed size on the glass, so their room is pixels whatever the zoom).
 */
export const fitView = (
  points: Pt[],
  size: { w: number; h: number },
  pad = { x: 100, y: 60 },
  screen = false,
): { k: number; x: number; y: number } | null => {
  if (!points.length) return null
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)]
  const fits = screen
    ? Math.min(
        Math.max(1, size.w - pad.x * 2) / Math.max(1, x1 - x0),
        Math.max(1, size.h - pad.y * 2) / Math.max(1, y1 - y0),
      )
    : Math.min(size.w / (x1 - x0 + pad.x * 2), size.h / (y1 - y0 + pad.y * 2))
  const k = Math.min(2, Math.max(0.3, fits))
  return { k, x: size.w / 2 - ((x0 + x1) / 2) * k, y: size.h / 2 - ((y0 + y1) / 2) * k }
}

/**
 * A view for a list (Approvals): as wide as the box allows, up to `maxK`, read
 * from its top left (a long list runs on below, to be panned to), never a
 * picture shrunk to fit its height. `pad` is in screen pixels.
 */
export const fitList = (
  points: Pt[],
  size: { w: number; h: number },
  pad: { left: number; right: number; top: number },
  maxK = 1.1,
): { k: number; x: number; y: number } | null => {
  if (!points.length) return null
  const xs = points.map((p) => p.x)
  const [x0, x1, y0] = [Math.min(...xs), Math.max(...xs), Math.min(...points.map((p) => p.y))]
  const k = Math.min(maxK, Math.max(0.3, (size.w - pad.left - pad.right) / Math.max(1, x1 - x0)))
  return { k, x: pad.left - x0 * k, y: pad.top - y0 * k }
}

/**
 * A size that stays readable on a small screen: `base` in graph units, but never
 * under `minPx` on the glass at zoom `k`, and never grown past `max` times itself
 * (so a far-out view stays a picture, not a blob).
 */
export const screenSize = (base: number, k: number, minPx: number, max = 2): number =>
  Math.min(base * max, Math.max(base, minPx / k))

export const MIN_ZOOM = 0.25
export const MAX_ZOOM = 3

export interface Touch2 {
  /** the two fingers when the pinch began, and the view then (container coordinates) */
  d: number
  mx: number
  my: number
  view: { k: number; x: number; y: number }
}

/** Where a pinch of two fingers (now at a, b) puts the view: zoom by the spread, follow the midpoint. */
export const pinchView = (start: Touch2, a: Pt, b: Pt): { k: number; x: number; y: number } => {
  const d = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y))
  const k = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (start.view.k * d) / Math.max(1, start.d)))
  // the graph point that sat under the first midpoint stays under the fingers' midpoint
  const wx = (start.mx - start.view.x) / start.view.k
  const wy = (start.my - start.view.y) / start.view.k
  return { k, x: (a.x + b.x) / 2 - wx * k, y: (a.y + b.y) / 2 - wy * k }
}

// ---- labels: fewer collisions -----------------------------------------------------

export interface LabelBox {
  id: string
  /** the label's centre, and the node's bottom edge it hangs from */
  x: number
  y: number
  w: number
  h: number
  /** the more important a label, the likelier it is kept */
  priority: number
  /** always drawn (an agent's name: a working agent with no name is a dot nobody can read); the rest keep clear of it */
  always?: boolean
}

/**
 * Which labels to draw: the most important first, each dropped when it would
 * cover one already kept or the body of a node (`bodies`, circles).
 */
export const declutterLabels = (
  labels: LabelBox[],
  bodies: { id: string; x: number; y: number; r: number }[] = [],
) => {
  const kept: LabelBox[] = []
  const shown = new Set<string>()
  const pad = 2
  const hitsLabel = (a: LabelBox, b: LabelBox) =>
    Math.abs(a.x - b.x) < (a.w + b.w) / 2 + pad && Math.abs(a.y - b.y) < (a.h + b.h) / 2 + pad
  const hitsBody = (a: LabelBox, b: { x: number; y: number; r: number }) =>
    Math.abs(a.x - b.x) < a.w / 2 + b.r && Math.abs(a.y - b.y) < a.h / 2 + b.r
  for (const l of [...labels].sort(
    (a, b) => Number(!!b.always) - Number(!!a.always) || b.priority - a.priority,
  )) {
    if (!l.always && kept.some((k) => hitsLabel(l, k))) continue
    if (!l.always && bodies.some((b) => b.id !== l.id && hitsBody(l, b))) continue
    kept.push(l)
    shown.add(l.id)
  }
  return shown
}

/** Graph and legend words for a load share (-1 unknown): "42%". */
export const loadWords = (share: number) => (share < 0 ? 'unknown' : `${Math.round(share * 100)}%`)

/** Whether Argo CD has an app off: anything but Synced and Healthy (the Graph's Infra view, the Infrastructure page). */
export const appIsOff = (a: { sync: string; health: string }) => a.health !== 'Healthy' || a.sync !== 'Synced'
